import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  BookOpen, Plus, Play, Trash2, Edit2, ChevronDown, ChevronUp,
  Server, AlertTriangle, CheckCircle, XCircle, Clock, Loader2,
  Copy, RotateCcw, Zap, CalendarClock, Power, PowerOff, Bot, ShieldCheck
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCHEDULE_PRESETS = [
  { value: "daily_2am", label: "Daily at 2 AM" },
  { value: "every_6h", label: "Every 6 hours" },
  { value: "every_12h", label: "Every 12 hours" },
  { value: "weekly_monday_2am", label: "Weekly Monday 2 AM" },
  { value: "custom", label: "Custom Cron" },
];

function RunbookEditor({ open, onClose, onSave, initial, servers }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState([{ command: "", description: "", server_id: "", os_type: "linux" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {
      setName(initial.name);
      setDescription(initial.description || "");
      setSteps(initial.steps?.length ? initial.steps : [{ command: "", description: "", server_id: "", os_type: "linux" }]);
    } else {
      setName(""); setDescription("");
      setSteps([{ command: "", description: "", server_id: "", os_type: "linux" }]);
    }
  }, [initial, open]);

  const updateStep = (idx, field, value) => {
    setSteps(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };
  const addStep = () => setSteps(prev => [...prev, { command: "", description: "", server_id: "", os_type: "linux" }]);
  const removeStep = (idx) => { if (steps.length > 1) setSteps(prev => prev.filter((_, i) => i !== idx)); };
  const moveStep = (idx, dir) => {
    const ns = [...steps]; const t = idx + dir;
    if (t < 0 || t >= ns.length) return;
    [ns[idx], ns[t]] = [ns[t], ns[idx]]; setSteps(ns);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    const validSteps = steps.filter(s => s.command.trim());
    if (validSteps.length === 0) { toast.error("At least one command is required"); return; }
    setSaving(true);
    try { await onSave({ name, description, steps: validSteps }); onClose(); }
    catch { /* toast handled by caller */ }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0c0c0e] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit Runbook" : "Create Runbook"}</DialogTitle>
          <DialogDescription className="text-zinc-400">Define a reusable multi-step command template</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Deploy release v2.0"
                className="bg-black/50 border-white/10 text-white" data-testid="runbook-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Description</Label>
              <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Steps for deploying..."
                className="bg-black/50 border-white/10 text-white" data-testid="runbook-desc-input" />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-zinc-400 text-xs">Steps ({steps.length})</Label>
              <Button size="sm" variant="ghost" onClick={addStep} className="text-blue-400 hover:text-blue-300 h-7 text-xs" data-testid="add-step-btn">
                <Plus className="w-3 h-3 mr-1" /> Add Step
              </Button>
            </div>
            {steps.map((step, idx) => (
              <div key={idx} className="p-3 bg-black/30 rounded-lg border border-white/5 space-y-2" data-testid={`step-${idx}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-500 font-mono">Step {idx + 1}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => moveStep(idx, -1)} disabled={idx === 0} className="p-1 text-zinc-500 hover:text-white disabled:opacity-30"><ChevronUp className="w-3 h-3" /></button>
                    <button onClick={() => moveStep(idx, 1)} disabled={idx === steps.length - 1} className="p-1 text-zinc-500 hover:text-white disabled:opacity-30"><ChevronDown className="w-3 h-3" /></button>
                    <button onClick={() => removeStep(idx)} className="p-1 text-zinc-500 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
                <Input value={step.command} onChange={e => updateStep(idx, "command", e.target.value)}
                  placeholder="e.g. systemctl restart nginx" className="bg-black/50 border-white/10 text-green-400 font-mono text-sm"
                  data-testid={`step-command-${idx}`} />
                <div className="grid grid-cols-3 gap-2">
                  <Input value={step.description} onChange={e => updateStep(idx, "description", e.target.value)}
                    placeholder="Description" className="bg-black/50 border-white/10 text-white text-xs col-span-1" />
                  <Select value={step.server_id || "none"} onValueChange={v => updateStep(idx, "server_id", v === "none" ? "" : v)}>
                    <SelectTrigger className="bg-black/50 border-white/10 text-white text-xs h-9"><SelectValue placeholder="Server" /></SelectTrigger>
                    <SelectContent className="bg-[#0c0c0e] border-white/10 z-[9999]">
                      <SelectItem value="none">No server</SelectItem>
                      {servers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={step.os_type} onValueChange={v => updateStep(idx, "os_type", v)}>
                    <SelectTrigger className="bg-black/50 border-white/10 text-white text-xs h-9"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0c0c0e] border-white/10 z-[9999]">
                      <SelectItem value="linux">Linux</SelectItem>
                      <SelectItem value="windows">Windows</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
          <Button onClick={handleSave} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="save-runbook-btn">
            {saving ? "Saving..." : initial ? "Update Runbook" : "Create Runbook"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ScheduleDialog({ open, onClose, runbook, onSaved }) {
  const [scheduleType, setScheduleType] = useState(runbook?.schedule_type || "daily_2am");
  const [cronExpr, setCronExpr] = useState(runbook?.cron_expression || "0 2 * * *");
  const [mode, setMode] = useState(runbook?.schedule_mode || "sequential");
  const [enabled, setEnabled] = useState(runbook?.schedule_enabled ?? true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (runbook) {
      setScheduleType(runbook.schedule_type || "daily_2am");
      setCronExpr(runbook.cron_expression || "0 2 * * *");
      setMode(runbook.schedule_mode || "sequential");
      setEnabled(runbook.schedule_enabled ?? true);
    }
  }, [runbook, open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/runbooks/${runbook.id}/schedule`, {
        schedule_type: scheduleType,
        cron_expression: scheduleType === "custom" ? cronExpr : null,
        enabled,
        mode,
      });
      toast.success(enabled ? "Schedule saved" : "Schedule disabled");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save schedule");
    } finally { setSaving(false); }
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/runbooks/${runbook.id}/schedule`);
      toast.success("Schedule removed");
      onSaved();
      onClose();
    } catch { toast.error("Failed to remove schedule"); }
    finally { setSaving(false); }
  };

  if (!runbook) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0c0c0e] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-purple-400" /> Schedule Runbook
          </DialogTitle>
          <DialogDescription className="text-zinc-400">{runbook.name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg border border-white/5">
            <div>
              <p className="text-sm text-white">Enabled</p>
              <p className="text-xs text-zinc-500">Toggle automatic execution</p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="schedule-enabled-toggle" />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs">Schedule</Label>
            <div className="grid grid-cols-1 gap-1.5">
              {SCHEDULE_PRESETS.map(p => (
                <button key={p.value} onClick={() => setScheduleType(p.value)}
                  className={`text-left px-3 py-2 rounded-lg border text-xs transition-all ${
                    scheduleType === p.value
                      ? "bg-purple-600/10 border-purple-500/30 text-purple-400"
                      : "bg-black/30 border-white/5 text-zinc-400 hover:border-white/15"
                  }`} data-testid={`schedule-${p.value}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {scheduleType === "custom" && (
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Cron Expression</Label>
              <Input value={cronExpr} onChange={e => setCronExpr(e.target.value)}
                placeholder="0 2 * * *" className="bg-black/50 border-white/10 text-white font-mono text-sm"
                data-testid="cron-input" />
              <p className="text-[10px] text-zinc-600">Format: minute hour day month day_of_week</p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-xs">Execution Mode</Label>
            <div className="flex gap-2">
              <button onClick={() => setMode("sequential")}
                className={`flex-1 px-3 py-2 rounded-lg border text-xs ${mode === "sequential" ? "bg-blue-600/10 border-blue-500/30 text-blue-400" : "bg-black/30 border-white/5 text-zinc-400"}`}
                data-testid="schedule-mode-sequential">Sequential</button>
              <button onClick={() => setMode("parallel")}
                className={`flex-1 px-3 py-2 rounded-lg border text-xs ${mode === "parallel" ? "bg-yellow-600/10 border-yellow-500/30 text-yellow-400" : "bg-black/30 border-white/5 text-zinc-400"}`}
                data-testid="schedule-mode-parallel">Parallel</button>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-purple-600 hover:bg-purple-700" data-testid="save-schedule-btn">
              {saving ? "Saving..." : "Save Schedule"}
            </Button>
            {runbook.schedule_enabled && (
              <Button onClick={handleRemove} disabled={saving} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="remove-schedule-btn">
                Remove
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RunResultsDialog({ open, onClose, results }) {
  if (!results) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0c0c0e] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Execution Results</DialogTitle>
          <DialogDescription className="text-zinc-400">
            {results.executed_count} executed, {results.queued_count} queued, {results.failed_count} failed
            {results.scheduled && " (scheduled run)"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {results.results?.map((r, i) => (
            <div key={i} className={`p-3 rounded-lg border ${
              r.status === "executed" && r.success ? "bg-green-500/5 border-green-500/20" :
              r.status === "queued" ? "bg-yellow-500/5 border-yellow-500/20" :
              r.status === "skipped" ? "bg-zinc-500/5 border-zinc-500/20" :
              "bg-red-500/5 border-red-500/20"
            }`} data-testid={`run-result-${i}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-zinc-500 font-mono">Step {r.step + 1}</span>
                <Badge className={`text-[10px] ${
                  r.status === "executed" && r.success ? "bg-green-500/20 text-green-400" :
                  r.status === "queued" ? "bg-yellow-500/20 text-yellow-400" :
                  r.status === "skipped" ? "bg-zinc-500/20 text-zinc-400" :
                  "bg-red-500/20 text-red-400"
                }`}>{r.status}</Badge>
              </div>
              <code className="text-sm text-green-400 font-mono">{r.command}</code>
              {r.description && <p className="text-xs text-zinc-500 mt-1">{r.description}</p>}
              {r.output && <pre className="mt-2 text-xs text-zinc-300 bg-black/40 p-2 rounded max-h-32 overflow-auto font-mono whitespace-pre-wrap">{r.output}</pre>}
              {r.error && <pre className="mt-1 text-xs text-red-400 font-mono">{r.error}</pre>}
              {r.message && <p className="mt-1 text-xs text-yellow-400">{r.message}</p>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Runbooks() {
  const { user } = useAuth();
  const [runbooks, setRunbooks] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [runResults, setRunResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [executing, setExecuting] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [autonomyCfg, setAutonomyCfg] = useState(null);

  const fetchData = async () => {
    try {
      const [rbRes, srvRes, acRes] = await Promise.all([
        axios.get(`${API}/runbooks`),
        axios.get(`${API}/servers`),
        axios.get(`${API}/autonomy/config`),
      ]);
      setRunbooks(rbRes.data);
      setServers(srvRes.data);
      setAutonomyCfg(acRes.data);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  const toggleGlobalAutonomy = async () => {
    try {
      const res = await axios.put(`${API}/autonomy/config`, { global_enabled: !autonomyCfg.global_enabled });
      setAutonomyCfg(res.data);
      toast.success(res.data.global_enabled ? "Autonomy resumed" : "All autonomy paused");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleRunbookAutonomy = async (rb) => {
    try {
      const res = await axios.post(`${API}/runbooks/${rb.id}/autonomy`, { enabled: !rb.autonomy_enabled });
      toast.success(res.data.autonomy_enabled ? "Autonomy enabled — this fix will auto-run on match" : "Autonomy disabled");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Not eligible for autonomy yet"); }
  };

  const isEligible = (rb) => autonomyCfg && rb.source === "auto_fix"
    && (rb.success_count || 0) >= autonomyCfg.min_successes
    && (rb.rollback_count || 0) <= autonomyCfg.max_rollbacks
    && (autonomyCfg.max_risk === "medium" ? ["low", "medium"] : ["low"]).includes(rb.risk_level);

  useEffect(() => { fetchData(); }, []);

  const handleSave = async (data) => {
    if (editTarget) {
      await axios.put(`${API}/runbooks/${editTarget.id}`, data);
      toast.success("Runbook updated");
    } else {
      await axios.post(`${API}/runbooks`, data);
      toast.success("Runbook created");
    }
    fetchData();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this runbook?")) return;
    try {
      await axios.delete(`${API}/runbooks/${id}`);
      toast.success("Runbook deleted");
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  const handleExecute = async (id, mode) => {
    setExecuting(id);
    try {
      const res = await axios.post(`${API}/runbooks/${id}/execute?mode=${mode}`);
      setRunResults(res.data);
      setShowResults(true);
      toast.success(`Runbook executed: ${res.data.executed_count} steps completed`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Execution failed");
    } finally { setExecuting(null); }
  };

  const getServerName = (id) => servers.find(s => s.id === id)?.name || "Unassigned";

  return (
    <div className="p-6 space-y-6" data-testid="runbooks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-mono text-white tracking-tight">Runbooks</h1>
          <p className="text-zinc-400 text-sm mt-1">Reusable multi-step command templates with scheduling</p>
        </div>
        <Button onClick={() => { setEditTarget(null); setEditorOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="create-runbook-btn">
          <Plus className="w-4 h-4 mr-2" /> New Runbook
        </Button>
      </div>

      {/* Global autonomy master switch */}
      {autonomyCfg && (
        <Card className={`border ${autonomyCfg.global_enabled ? "border-emerald-500/20 bg-emerald-500/5" : "border-amber-500/20 bg-amber-500/5"}`} data-testid="autonomy-master">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShieldCheck className={`w-5 h-5 ${autonomyCfg.global_enabled ? "text-emerald-400" : "text-amber-400"}`} />
              <div>
                <p className="text-white text-sm font-medium">Autonomous Fixes {autonomyCfg.global_enabled ? "Active" : "Paused"}</p>
                <p className="text-xs text-zinc-500">
                  Proven runbooks (≥{autonomyCfg.min_successes} successes, 0 rollbacks, {autonomyCfg.max_risk}-or-lower risk) with autonomy on will auto-run on matching tickets.
                </p>
              </div>
            </div>
            <Switch checked={autonomyCfg.global_enabled} onCheckedChange={toggleGlobalAutonomy} data-testid="global-autonomy-switch" />
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-blue-400 animate-spin" /></div>
      ) : runbooks.length === 0 ? (
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardContent className="py-16 text-center">
            <BookOpen className="w-14 h-14 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No runbooks yet</h3>
            <p className="text-zinc-400 text-sm mb-4">Create your first runbook to automate multi-step tasks</p>
            <Button onClick={() => { setEditTarget(null); setEditorOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="create-first-runbook">
              <Plus className="w-4 h-4 mr-2" /> Create Runbook
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {runbooks.map(rb => (
            <Card key={rb.id} className="bg-[#0c0c0e] border-white/5 hover:border-white/10 transition-colors" data-testid={`runbook-${rb.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpandedId(expandedId === rb.id ? null : rb.id)}>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <BookOpen className="w-4 h-4 text-blue-400 shrink-0" />
                      <h3 className="text-white font-medium truncate">{rb.name}</h3>
                      <Badge className="text-[10px] bg-zinc-800 text-zinc-400 border-zinc-700">{rb.steps?.length || 0} steps</Badge>
                      {rb.run_count > 0 && <Badge className="text-[10px] bg-purple-500/10 text-purple-400 border-purple-500/20">{rb.run_count} runs</Badge>}
                      {rb.source === "auto_fix" && (
                        <Badge className="text-[10px] bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                          <CheckCircle className="w-2.5 h-2.5 mr-1" /> {rb.success_count || 0}× proven
                        </Badge>
                      )}
                      {rb.autonomy_enabled && (
                        <Badge className="text-[10px] bg-cyan-500/10 text-cyan-300 border-cyan-500/20" data-testid={`autonomy-badge-${rb.id}`}>
                          <Bot className="w-2.5 h-2.5 mr-1" /> Autonomous
                        </Badge>
                      )}
                      {rb.schedule_enabled && (
                        <Badge className="text-[10px] bg-purple-500/10 text-purple-300 border-purple-500/20" data-testid={`schedule-badge-${rb.id}`}>
                          <CalendarClock className="w-2.5 h-2.5 mr-1" /> {rb.schedule_label || "Scheduled"}
                        </Badge>
                      )}
                    </div>
                    {rb.description && <p className="text-xs text-zinc-500 mb-1">{rb.description}</p>}
                    <div className="text-[11px] text-zinc-600">
                      by {rb.created_by_email} &middot; {new Date(rb.created_at).toLocaleDateString()}
                      {rb.last_run && <> &middot; Last run: {new Date(rb.last_run).toLocaleDateString()}</>}
                      {rb.next_run && <> &middot; Next: {new Date(rb.next_run).toLocaleString()}</>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {rb.source === "auto_fix" && (isEligible(rb) || rb.autonomy_enabled) && (
                      <Button size="sm" variant="outline" onClick={() => toggleRunbookAutonomy(rb)}
                        className={`text-xs h-8 ${rb.autonomy_enabled ? "border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10" : "border-white/10 text-zinc-400 hover:bg-white/5"}`}
                        data-testid={`autonomy-toggle-${rb.id}`}>
                        <Bot className="w-3 h-3 mr-1" /> {rb.autonomy_enabled ? "Autonomous" : "Enable Autonomy"}
                      </Button>
                    )}
                    <Button size="sm" onClick={() => handleExecute(rb.id, "sequential")} disabled={executing === rb.id}
                      className="bg-green-600 hover:bg-green-700 text-white text-xs h-8" data-testid={`run-sequential-${rb.id}`}>
                      {executing === rb.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}
                      Run
                    </Button>
                    <Button size="sm" onClick={() => handleExecute(rb.id, "parallel")} disabled={executing === rb.id}
                      variant="outline" className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 text-xs h-8" data-testid={`run-parallel-${rb.id}`}>
                      <Zap className="w-3 h-3 mr-1" /> Parallel
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setScheduleTarget(rb); setScheduleOpen(true); }}
                      className={`text-xs h-8 ${rb.schedule_enabled ? "border-purple-500/30 text-purple-400 hover:bg-purple-500/10" : "border-white/10 text-zinc-400 hover:bg-white/5"}`}
                      data-testid={`schedule-btn-${rb.id}`}>
                      <CalendarClock className="w-3 h-3 mr-1" /> Schedule
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditTarget(rb); setEditorOpen(true); }}
                      className="text-zinc-400 hover:text-white h-8 w-8 p-0" data-testid={`edit-runbook-${rb.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(rb.id)}
                      className="text-zinc-400 hover:text-red-400 h-8 w-8 p-0" data-testid={`delete-runbook-${rb.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>

                {expandedId === rb.id && rb.steps && (
                  <div className="mt-3 pt-3 border-t border-white/5 space-y-1.5">
                    {rb.steps.map((step, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 bg-black/20 rounded-lg">
                        <span className="text-[10px] text-zinc-600 font-mono w-4 shrink-0">{i + 1}</span>
                        <code className="text-xs text-green-400 font-mono flex-1 truncate">{step.command}</code>
                        {step.server_id && (
                          <span className="text-[10px] text-zinc-500 flex items-center gap-1 shrink-0">
                            <Server className="w-2.5 h-2.5" /> {getServerName(step.server_id)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <RunbookEditor open={editorOpen} onClose={() => setEditorOpen(false)} onSave={handleSave} initial={editTarget} servers={servers} />
      <RunResultsDialog open={showResults} onClose={() => setShowResults(false)} results={runResults} />
      <ScheduleDialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} runbook={scheduleTarget} onSaved={fetchData} />
    </div>
  );
}
